import { useEffect, useState } from 'react';
import { useFetch } from './components/common.tsx';
import { useSession } from './session.ts';

// Getting-started steps and their progress. Server facts (trades, journaled trades, data key) come from
// /api/onboarding; "opened a trade" and "looked around" are remembered in this browser.

type Facts = { trades: number; latestTradeId: number | null; journaled: number; hasMarketDataKey: boolean };
export type Step = { key: string; title: string; body: string; done: boolean; optional?: boolean; action?: { to: string; label: string } };

const SEEN_KEY = 'tj_seen_pages';
const HIDE_KEY = 'tj_start_hidden';
const EXPLORE = ['/calendar', '/performance', '/research'];

function readSeen(): string[] {
  try { return JSON.parse(localStorage.getItem(SEEN_KEY) ?? '[]'); } catch { return []; }
}

/** Remember which sections have been visited (call on navigation). */
export function noteVisit(pathname: string) {
  const root = pathname.startsWith('/trades/') ? '/trades/:id' : `/${pathname.split('/')[1] ?? ''}`;
  const seen = readSeen();
  if (seen.includes(root)) return;
  try { localStorage.setItem(SEEN_KEY, JSON.stringify([...seen, root])); } catch { /* ignore */ }
}

export function useOnboarding() {
  const { me } = useSession();
  const { data } = useFetch<Facts>('/api/onboarding');
  const [hidden, setHidden] = useState(() => { try { return localStorage.getItem(HIDE_KEY) === '1'; } catch { return false; } });
  const [seen, setSeen] = useState(readSeen);
  useEffect(() => { setSeen(readSeen()); }, [data]);
  const explored = EXPLORE.filter((p) => seen.includes(p)).length;

  const steps: Step[] = [
    {
      key: 'import', title: 'Import your trades',
      body: 'Export your trade history from your broker (TradeZero, Interactive Brokers, thinkorswim, Webull or DAS Trader) and drop it on the Import page; it shows how for each. Edgebook groups your fills into trades and fetches a chart for each one.',
      done: (data?.trades ?? 0) > 0, action: { to: '/import', label: 'Go to Import' },
    },
    {
      key: 'trade', title: 'Open a trade',
      body: 'Each trade has a chart with your fills, avg entry/exit lines and indicators, a catalyst line with the news or SEC filing behind the move, N/S badges for news and splits, and a replay that plays the trade back candle by candle.',
      done: seen.includes('/trades/:id'),
      action: data?.latestTradeId ? { to: `/trades/${data.latestTradeId}`, label: 'Open your latest trade' } : undefined,
    },
    {
      key: 'journal', title: 'Journal it',
      body: 'Add notes, a setup and a 1–5 rating. Edgebook suggests a first draft of the notes from the chart and news; edit it and save. Each morning, Today\'s "Finish yesterday" walks you through the last session\'s trades, and the count on Today shows what\'s left from the last 30 days.',
      done: (data?.journaled ?? 0) > 0,
      action: data?.latestTradeId ? { to: `/trades/${data.latestTradeId}`, label: 'Journal a trade' } : undefined,
    },
    {
      key: 'secure', title: 'Secure your account',
      body: 'Turn on Face ID / Windows Hello for one-tap sign-in, and keep recovery codes so you can reset your password yourself if you forget it.',
      done: !!me && me.passkeys > 0 && me.recoveryCodesLeft > 0, action: { to: '/settings', label: 'Open Settings' },
    },
    {
      key: 'explore', title: 'Look around',
      body: 'Calendar shows your days in green and red. Performance has your numbers vs the period before, a Breakdown by setup, time of day and more, and a Discipline view that replays your trades under different rules and flags revenge trades. Research pulls a ticker\'s filings, dilution risk and cash runway.',
      done: explored >= 2, action: { to: '/performance', label: 'Open Performance' },
    },
    {
      key: 'data', title: 'Add a free market data key', optional: true,
      body: 'A free Massive (Polygon) key in Settings adds pre-market and after-hours volume, two years of 1-minute charts, and another news source.',
      done: !!data?.hasMarketDataKey, action: { to: '/settings', label: 'Add a key' },
    },
  ];
  const required = steps.filter((s) => !s.optional);
  return {
    data, steps, hidden,
    done: required.filter((s) => s.done).length,
    total: required.length,
    hide: () => { setHidden(true); try { localStorage.setItem(HIDE_KEY, '1'); } catch { /* ignore */ } },
  };
}
